import { NavLink } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useRecoilValue } from "recoil";
import {
  Avatar,
  ListItemIcon,
  ListItemText,
  MenuList,
  SvgIcon,
  Box,
  MenuItem,
  Divider,
  Typography,
  Popper,
  ClickAwayListener,
  Paper,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

import { stringAvatar } from "./utils";
import { LogoutIcon } from "../../components/icons";
import { DRAWER_WIDTH_OPEN } from "../utils";
import { currentUserAtom } from "../../AuthProvider";

const ProfileMenu = ({
  anchorEl,
  onClose,
  onOpenProfileSettings,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onOpenProfileSettings: () => void;
}) => {
  const intl = useIntl();
  const currentUser = useRecoilValue(currentUserAtom);
  const openProfilMenu = Boolean(anchorEl);

  return (
    <Popper
      open={openProfilMenu}
      anchorEl={anchorEl}
      placement="top"
      disablePortal
      sx={{ width: DRAWER_WIDTH_OPEN - 8 }}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 10],
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            background: theme.vars.palette.menu.background,
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            borderRadius: 1,
            padding: "1px 0",
          })}
        >
          <Box
            sx={{
              display: "flex",
              margin: 1,
              gap: 1,
              alignItems: "center",
            }}
          >
            <Avatar
              alt={
                currentUser
                  ? `${currentUser.first_name} ${currentUser.last_name}`
                  : ""
              }
              src={currentUser?.profile_picture_url ?? undefined}
              {...(!currentUser?.profile_picture_url
                ? stringAvatar(
                    currentUser
                      ? `${currentUser.first_name} ${currentUser.last_name}`
                      : ""
                  )
                : {})}
              sx={(theme) => ({
                background: currentUser?.profile_picture_url
                  ? "transparent"
                  : theme.vars.palette.nancie.gradient,
                color: "menu.text",
                fontSize: "0.875rem",
                fontWeight: "bold",
                height: "34px",
                width: "34px",
              })}
            />
            <Box>
              <Typography sx={{ fontSize: "0.875rem", color: "menu.text" }}>
                {currentUser?.first_name} {currentUser?.last_name}
              </Typography>
              <Typography
                sx={{ color: "menu.text", opacity: 0.7, fontSize: "0.75rem" }}
              >
                {currentUser?.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1, borderColor: "menu.divider" }} />

          <MenuList disablePadding>
            <MenuItem disableRipple onClick={onOpenProfileSettings}>
              <ListItemIcon sx={{ color: "menu.text" }}>
                <ManageAccountsIcon />
              </ListItemIcon>
              <ListItemText sx={{ color: "menu.text" }}>
                <FormattedMessage id="PROFILE_SETTINGS" />
              </ListItemText>
            </MenuItem>
            <MenuItem
              disableRipple
              component={NavLink}
              to="/logout"
              onClick={onClose}
            >
              <ListItemIcon sx={{ color: "menu.text" }}>
                <SvgIcon fontSize="small">
                  <LogoutIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({ id: "DISCONNECT" })}
                sx={{ color: "menu.text" }}
              />
            </MenuItem>
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default ProfileMenu;
